import React from "react";
import * as url from "helper/UrlHelper";
import { assetImages } from "constants";

const PatientDetailsHeader = ({ patientData, isLoading, error }) => {
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Not provided";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Format phone number for display
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return "Not provided";
    return phone.replace(/(\d{1})(\d{5})(\d{5})/, "+$1 $2 $3");
  };

  if (isLoading) {
    return (
      <ul className="d-flex align-items-center gap-3 px-3 py-2 bg-white text-nowrap rounded-8 shadow-sm overflow-x-auto mb-4">
        {/* Custom ID Placeholder */}
        <li className="flex-fill">
          <div className="d-flex align-items-center gap-2">
            <div className="placeholder-glow">
              <span
                className="placeholder bg-primary rounded-circle"
                style={{ width: "24px", height: "24px" }}
              ></span>
            </div>
            <div className="placeholder-glow">
              <span
                className="placeholder bg-secondary"
                style={{ width: "80px", height: "16px" }}
              ></span>
            </div>
          </div>
        </li>

        {/* Insurance Placeholder */}
        <li className="flex-fill">
          <div className="d-flex align-items-center gap-2">
            <div className="placeholder-glow">
              <span
                className="placeholder bg-success rounded-circle"
                style={{ width: "24px", height: "24px" }}
              ></span>
            </div>
            <div className="placeholder-glow">
              <span
                className="placeholder bg-secondary"
                style={{ width: "100px", height: "16px" }}
              ></span>
            </div>
          </div>
        </li>

        {/* Patient Name Placeholder */}
        <li className="flex-fill d-flex align-items-center gap-2">
          <div className="placeholder-glow">
            <span
              className="placeholder bg-light rounded-circle"
              style={{ width: "24px", height: "24px" }}
            ></span>
          </div>
          <div className="placeholder-glow">
            <span
              className="placeholder bg-secondary"
              style={{ width: "120px", height: "16px" }}
            ></span>
          </div>
        </li>

        {/* Date Placeholder */}
        <li className="flex-fill d-flex align-items-center gap-2">
          <div className="placeholder-glow">
            <span
              className="placeholder bg-secondary rounded-circle"
              style={{ width: "24px", height: "24px" }}
            ></span>
          </div>
          <div className="placeholder-glow">
            <span
              className="placeholder bg-secondary"
              style={{ width: "90px", height: "16px" }}
            ></span>
          </div>
        </li>

        {/* Email Placeholder */}
        <li className="flex-fill">
          <div className="d-flex align-items-center gap-2">
            <div className="placeholder-glow">
              <span
                className="placeholder bg-primary rounded-circle"
                style={{ width: "24px", height: "24px" }}
              ></span>
            </div>
            <div className="placeholder-glow">
              <span
                className="placeholder bg-secondary"
                style={{ width: "140px", height: "16px" }}
              ></span>
            </div>
          </div>
        </li>

        {/* Phone Placeholder */}
        <li className="flex-fill">
          <div className="d-flex align-items-center gap-2">
            <div className="placeholder-glow">
              <span
                className="placeholder bg-primary rounded-circle"
                style={{ width: "24px", height: "24px" }}
              ></span>
            </div>
            <div className="placeholder-glow">
              <span
                className="placeholder bg-secondary"
                style={{ width: "110px", height: "16px" }}
              ></span>
            </div>
          </div>
        </li>

        {/* WhatsApp Placeholder */}
        <li className="flex-fill">
          <div className="d-flex align-items-center gap-2">
            <div className="placeholder-glow">
              <span
                className="placeholder bg-success rounded-circle"
                style={{ width: "24px", height: "24px" }}
              ></span>
            </div>
          </div>
        </li>
      </ul>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger rounded-8 mb-4" role="alert">
        <span className="material-symbols-outlined align-middle me-2">
          error
        </span>
        {error}
      </div>
    );
  }

  return (
    <ul className="d-flex align-items-center gap-3 px-3 py-2 bg-white text-nowrap rounded-8 shadow-sm overflow-x-auto mb-4">
      {/* Custom ID */}
      <li className="flex-fill">
        <div className="d-flex align-items-center gap-2">
          <span className="material-symbols-outlined icon-lg text-primary">
            badge
          </span>
          <span className="fw-semibold">{patientData.customId}</span>
        </div>
      </li>

      {/* Insurance/Company */}
      <li className="flex-fill">
        <div className="d-flex align-items-center gap-2">
          <span className="material-symbols-outlined icon-lg text-success">
            security
          </span>
          <span>{patientData.insurance}</span>
        </div>
      </li>

      {/* Patient Name with Avatar */}
      <li className="flex-fill d-flex align-items-center gap-2">
        <div
          className="avatar rounded-circle overflow-hidden"
          style={{ width: "1.5rem", height: "1.5rem" }}
        >
          <img
            src={
              patientData.profileImage
                ? url.SERVER_URL + patientData.profileImage
                : assetImages.defaultUser
            }
            alt={`${patientData.name} ${patientData.surname}`}
            className="w-100 h-100 object-fit-cover object-center"
          />
        </div>
        <p className="mb-0 fw-medium">
          {`${patientData.name} ${patientData.surname}`.trim() ||
            "Unknown Patient"}
        </p>
      </li>

      {/* Date of Birth */}
      <li className="flex-fill d-flex align-items-center gap-2">
        <span className="d-block material-symbols-outlined icon-lg text-gray">
          calendar_today
        </span>
        <div>
          <span className="d-block">{formatDate(patientData.dateOfBirth)}</span>
        </div>
      </li>

      {/* Email */}
      <li className="flex-fill">
        <a
          href={patientData.email ? `mailto:${patientData.email}` : "#"}
          className={`d-flex align-items-center gap-2 ${
            !patientData.email ? "text-muted" : ""
          }`}
          style={{ textDecoration: "none" }}
        >
          <span className="d-block material-symbols-outlined icon-lg text-primary">
            email
          </span>
          <span className="d-block text-truncate" style={{ maxWidth: "150px" }}>
            {patientData.email || "No email"}
          </span>
        </a>
      </li>

      {/* Phone */}
      <li className="flex-fill">
        <a
          href={patientData.phone ? `tel:${patientData.phone}` : "#"}
          className={`d-flex align-items-center gap-2 ${
            !patientData.phone ? "text-muted" : ""
          }`}
          style={{ textDecoration: "none" }}
        >
          <span className="d-block material-symbols-outlined icon-lg text-primary">
            phone_in_talk
          </span>
          <span className="d-block">
            {formatPhoneForDisplay(patientData.phone)}
          </span>
        </a>
      </li>

      {/* WhatsApp */}
      <li className="flex-fill">
        {patientData.whatsappNumber ? (
          <a
            href={`https://wa.me/${patientData.whatsappNumber.replace(
              /\D/g,
              ""
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="d-flex align-items-center gap-2"
            title="Contact via WhatsApp"
          >
            <img
              src="/assets/img/whatsapp.svg"
              alt="WhatsApp"
              className="img-fluid"
              style={{ width: "24px" }}
            />
          </a>
        ) : (
          <div className="d-flex align-items-center gap-2 text-muted">
            <img
              src="/assets/img/whatsapp.svg"
              alt="WhatsApp"
              className="img-fluid opacity-50"
              style={{ width: "24px" }}
            />
          </div>
        )}
      </li>

      {/* Blood Group (if available) */}
      {patientData.bloodGroup && (
        <li className="flex-fill d-flex align-items-center gap-2">
          <span className="material-symbols-outlined icon-lg text-danger">
            water_drop
          </span>
          <div>
            <small className="d-block text-muted">Blood</small>
            <span className="d-block fw-semibold">
              {patientData.bloodGroup}
            </span>
          </div>
        </li>
      )}

      {/* Emergency Contact (if available) */}
      {patientData.emergencyContact && (
        <li className="flex-fill d-flex align-items-center gap-2">
          <span className="material-symbols-outlined icon-lg text-warning">
            emergency
          </span>
          <div>
            <small className="d-block text-muted">Emergency</small>
            <span className="d-block">{patientData.emergencyContact}</span>
          </div>
        </li>
      )}
    </ul>
  );
};

export default PatientDetailsHeader;
